import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { useNavigate } from 'react-router-dom';
import { Phone, AlertCircle, Siren, Flame, Trash2, FireExtinguisher } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

const Scenarios = () => {
  const [scenarios, setScenarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('all');
  const [activeLanguage, setActiveLanguage] = useState('all');
  const [deletingScenarioId, setDeletingScenarioId] = useState(null);
  const navigate = useNavigate();
  
  useEffect(() => {
    fetchScenarios();
  }, []);
  
  const fetchScenarios = async () => {
    try {
      const response = await axios.get(`${API}/scenarios`);
      setScenarios(response.data);
    } catch (error) {
      console.error('Error fetching scenarios:', error);
      toast.error('Erreur lors du chargement des scénarios');
    } finally {
      setLoading(false);
    }
  };
  
  const startTraining = (scenarioId, language) => {
    // Store the selected language for this training session
    console.log('🔵 Setting language to:', language);
    sessionStorage.setItem('selected_training_language', language);
    console.log('🔵 SessionStorage set. Navigating to training...');
    navigate(`/training/${scenarioId}`);
  };

  const deleteScenario = async (scenarioId, scenarioTitle) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer le scénario "${scenarioTitle}"? Cette action est irréversible.`)) {
      return;
    }

    setDeletingScenarioId(scenarioId);
    try {
      await axios.delete(`${API}/scenarios/${scenarioId}`);
      setScenarios(scenarios.filter(s => s.id !== scenarioId));
      toast.success('Scénario supprimé avec succès');
    } catch (error) {
      console.error('Error deleting scenario:', error);
      toast.error('Erreur lors de la suppression du scénario');
    } finally {
      setDeletingScenarioId(null);
    }
  };
  
  const getCategoryIcon = (category) => {
    switch(category) {
      case 'police': return Phone;
      case 'ambulance': return AlertCircle;
      case 'incendie': return FireExtinguisher;
      default: return Siren;
    }
  };
  
  const getCategoryColor = (category) => {
    switch(category) {
      case 'police': return 'cm-cat-police';
      case 'ambulance': return 'cm-cat-ambulance';
      case 'incendie': return 'cm-cat-incendie';
      default: return 'cm-cat-default';
    }
  };
  
  const getSeverityColor = (severity) => {
    switch(severity) {
      case 'critical': return 'bg-red-100 text-red-700 border-red-300';
      case 'high': return 'bg-orange-100 text-orange-700 border-orange-300';
      case 'medium': return 'bg-amber-100 text-amber-700 border-amber-300';
      case 'low': return 'bg-emerald-100 text-emerald-700 border-emerald-300';
      default: return 'bg-gray-100 text-gray-700 border-gray-300';
    }
  };
  
  const getSeverityLabel = (severity) => {
    const labels = {
      critical: 'Critique',
      high: 'Élevé',
      medium: 'Moyen',
      low: 'Faible'
    };
    return labels[severity] || severity;
  };
  
  const filteredScenarios = scenarios.filter(s => {
    const categoryMatch = activeCategory === 'all' || s.category === activeCategory;
    const languageMatch = activeLanguage === 'all' || (s.language || 'french') === activeLanguage;
    return categoryMatch && languageMatch;
  });
  
  if (loading) {
    return (
      <div data-testid="scenarios-loading" className="flex items-center justify-center min-h-screen">
        <div className="animate-pulse text-xl" style={{ color: '#2E6CF5' }}>Chargement des scénarios...</div>
      </div>
    );
  }
  
  return (
    <div data-testid="scenarios-page" className="pt-20 px-4 sm:px-6 lg:px-8 pb-12 max-w-7xl mx-auto">
      <div className="mb-8">
        <h1 className="text-4xl sm:text-5xl font-bold mb-2" style={{ color: '#002768' }}>Scénarios d'Urgence</h1>
        <p className="text-lg text-gray-600">Choisissez un scénario pour commencer votre entraînement</p>
      </div>
      
      {/* Filters */}
      <div className="mb-8 space-y-4">
        {/* Category Filter */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Catégorie:</label>
          <Tabs defaultValue="all" onValueChange={setActiveCategory}>
            <TabsList data-testid="category-tabs" className="glass">
              <TabsTrigger value="all" data-testid="tab-all">Tous ({scenarios.length})</TabsTrigger>
              <TabsTrigger value="police" data-testid="tab-police">
                Police ({scenarios.filter(s => s.category === 'police').length})
              </TabsTrigger>
              <TabsTrigger value="ambulance" data-testid="tab-ambulance">
                Ambulance ({scenarios.filter(s => s.category === 'ambulance').length})
              </TabsTrigger>
              <TabsTrigger value="incendie" data-testid="tab-incendie">
                Incendie ({scenarios.filter(s => s.category === 'incendie').length})
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
        
        {/* Language Filter */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Langue:</label>
          <Tabs defaultValue="all" onValueChange={setActiveLanguage}>
            <TabsList data-testid="language-tabs" className="glass">
              <TabsTrigger value="all" data-testid="tab-all-lang">
                Toutes ({scenarios.length})
              </TabsTrigger>
              <TabsTrigger value="french" data-testid="tab-french">
                🇫🇷 Français ({scenarios.filter(s => (s.language || 'french') === 'french').length})
              </TabsTrigger>
              <TabsTrigger value="english" data-testid="tab-english">
                EN - English ({scenarios.filter(s => s.language === 'english').length})
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
      </div>
      
      {/* Scenarios Grid */}
      <div data-testid="scenarios-grid" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredScenarios.map((scenario) => {
          const Icon = getCategoryIcon(scenario.category);
          return (
            <Card 
              key={scenario.id} 
              data-testid={`scenario-card-${scenario.id}`}
              className="glass border-0 hover:shadow-xl cursor-pointer group"
              onClick={() => startTraining(scenario.id)}
            >
              <CardHeader>
                <div className="flex items-start justify-between mb-3">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform"
                       style={{ background: scenario.category === 'police' ? 'linear-gradient(135deg, #002768, #2E6CF5)' :
                                scenario.category === 'ambulance' ? 'linear-gradient(135deg, #24B219, #82F486)' :
                                scenario.category === 'incendie' ? 'linear-gradient(135deg, #DC2626, #B91C1C)' :
                                'linear-gradient(135deg, #DADADA, #999)' }}>
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex flex-col space-y-1">
                    <Badge 
                      variant="outline" 
                      className={`${getSeverityColor(scenario.severity)} font-semibold`}
                      data-testid={`severity-${scenario.id}`}
                    >
                      {getSeverityLabel(scenario.severity)}
                    </Badge>
                    <Badge 
                      variant="outline" 
                      className="bg-blue-50 text-blue-700 border-blue-300 font-semibold text-xs"
                    >
                      {(scenario.language || 'french') === 'french' ? '🇫🇷 FR' : 'EN'}
                    </Badge>
                  </div>
                </div>
                <CardTitle className="text-xl leading-tight">{scenario.title}</CardTitle>
                <CardDescription className="text-xs uppercase tracking-wide font-semibold">
                  {scenario.category}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-gray-600 mb-4 line-clamp-3">{scenario.description}</p>
                <div className="space-y-2 text-xs">
                  <div className="flex items-start space-x-2">
                    <span className="font-semibold text-gray-700 min-w-[60px]">Lieu:</span>
                    <span className="text-gray-600">{scenario.location}</span>
                  </div>
                </div>
                <div className="mt-4 flex space-x-2">
                  {/* French Button */}
                  <button
                    data-testid={`start-scenario-fr-${scenario.id}`}
                    className="flex-1 py-2 text-white rounded-lg font-semibold hover:shadow-lg flex items-center justify-center space-x-1"
                    style={{ background: 'linear-gradient(to right, #002768, #2E6CF5)' }}
                    onClick={(e) => {
                      e.stopPropagation();
                      startTraining(scenario.id, 'french');
                    }}
                  >
                    <span>🇫🇷</span>
                    <span>FR</span>
                  </button>
                  
                  {/* English Button */}
                  <button
                    data-testid={`start-scenario-en-${scenario.id}`}
                    className="flex-1 py-2 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-lg font-semibold hover:shadow-lg flex items-center justify-center space-x-1"
                    onClick={(e) => {
                      e.stopPropagation();
                      startTraining(scenario.id, 'english');
                    }}
                  >
                    <span>EN</span>
                  </button>
                  <button
                    data-testid={`delete-scenario-${scenario.id}`}
                    disabled={deletingScenarioId === scenario.id}
                    className="px-3 py-2 bg-rose-500 text-white rounded-lg font-semibold hover:bg-rose-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    onClick={(e) => {
                      e.stopPropagation();
                      deleteScenario(scenario.id, scenario.title);
                    }}
                    title="Supprimer ce scénario"
                  >
                    {deletingScenarioId === scenario.id ? (
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    ) : (
                      <Trash2 className="w-5 h-5" />
                    )}
                  </button>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
      
      {filteredScenarios.length === 0 && (
        <div className="text-center py-12">
          <p className="text-gray-500 text-lg">Aucun scénario disponible dans cette catégorie.</p>
        </div>
      )}
    </div>
  );
};

export default Scenarios;